import { useRouter } from '@tanstack/react-router';
import {
  Box,
  Flex,
  FormattedRelativeTime,
  Heading,
  Link,
  Panel,
  PanelSection,
  Paragraph,
  Text,
} from '@customerio/pluma-components/react';
import type { SqlImportWithDatabase } from '@/services/db/sql-import/entity';
import { isEmptyString } from '@/utils/string';
import { DatabaseDetails } from './database-details';
import { SyncStatusBadge } from './sync-status-badge';
import { SQL_SYNC_IMPORT_ROUTE_PATHS, SQL_SYNC_NEW_ROUTE_PATHS } from '../hooks';
import * as styles from './sql-import-list-item.css';

type SqlImportListItemProps = {
  workspaceId: string;
  sqlImport: SqlImportWithDatabase;
};

/** Where the name links: a draft reopens the create wizard on itself, an activated import opens its detail. */
function useSqlImportHref(workspaceId: string, sqlImport: SqlImportWithDatabase): string {
  const router = useRouter();
  const type = sqlImport.database?.type ?? '';
  if (sqlImport.is_draft) {
    return router.buildLocation({
      to: SQL_SYNC_NEW_ROUTE_PATHS.migrated,
      params: { workspaceId, type },
      search: { id: Number(sqlImport.id) },
    }).href;
  }
  return router.buildLocation({
    to: SQL_SYNC_IMPORT_ROUTE_PATHS.migrated,
    params: { workspaceId, type, sqlImportId: sqlImport.id },
  }).href;
}

export function SqlImportListItem({ workspaceId, sqlImport }: SqlImportListItemProps) {
  const { database } = sqlImport;
  const href = useSqlImportHref(workspaceId, sqlImport);

  return (
    <Panel data-test-sql-import-item>
      <PanelSection>
        <Box display="grid" alignItems="center" gap="300" className={styles.item}>
          <Box className={styles.name}>
            <Heading level="3" color="bold" mb="050" data-test-sql-import-item-name>
              <Link href={href} variant="primary" isUnderlined={false}>
                {isEmptyString(sqlImport.name) ? (
                  <Text color="subtle">No name</Text>
                ) : (
                  <Text truncate>{sqlImport.name}</Text>
                )}
              </Link>
            </Heading>

            {sqlImport.last_synced_at === 0 ? null : (
              <Paragraph variant="secondary" mt="100" data-test-sql-import-item-last-imported>
                Last synced{' '}
                <FormattedRelativeTime
                  date={new Date(sqlImport.last_synced_at * 1000)}
                  shouldUppercaseFirst={false}
                  withTooltip
                />
              </Paragraph>
            )}
          </Box>

          <Flex alignItems="center" className={styles.status}>
            <SyncStatusBadge sqlImport={sqlImport} />
          </Flex>

          <Flex alignItems="center" className={styles.database}>
            {database == null || isEmptyString(database.name) ? null : (
              <DatabaseDetails database={database} location={sqlImport.location} />
            )}
          </Flex>

          <Flex justifyContent="flex-end" className={styles.actions} />
        </Box>
      </PanelSection>
    </Panel>
  );
}
